import { DatePipe } from '@angular/common';
import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { FileDocument } from '@lib/models/file-document.model';
import { FetchDerGQL, FieldToComplete, ForceDerUpdateGQL, GedDocumentType, Investisseur } from '@lib/models/generated/graphql';
import { DocumentsService } from '@lib/services/documents.service';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { QueryManagerService } from 'finzzle-angular-utils';
export type DerResponse = {
  fileDocument?: FileDocument;
  fieldsToComplete?: FieldToComplete[];
};
@Injectable({
  providedIn: 'root',
})
export class DERStepService {
  private readonly fetchDerGQL = inject(FetchDerGQL);
  private readonly forceDERUpdateGQL = inject(ForceDerUpdateGQL);
  private documentService = inject(DocumentsService);
  private queryService = inject(QueryManagerService);
  private investisseurService = inject(InvestisseurService);
  private readonly datePipe = inject(DatePipe);
  private readonly derDocumentDescription = 'Document entrée en relation';
  public readonly isGestionPrivee = signal<boolean>(false);
  public async loadUnsignedDocument(withInvestisseur: boolean = true): Promise<DerResponse | null> {
    const derData = await this.queryService.handleMutationErrorsAsync(
      this.fetchDerGQL.mutate({
        variables: { consultantCode: withInvestisseur ? this.investisseurService.consultantCode() : null },
      })
    );
    this.isGestionPrivee.set(derData?.data?.existingDEROrGenerateIt?.isGestionPrivee ?? false);
    if (derData?.data?.existingDEROrGenerateIt) {
      if (isDevMode()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      const storageFile = derData.data.existingDEROrGenerateIt?.storageFiles?.[0];
      const fieldsToComplete = derData.data.existingDEROrGenerateIt?.fieldsToComplete;
      if (!storageFile) return { fieldsToComplete: fieldsToComplete ?? [] };
      const url = await this.fetchAndCacheDocument(storageFile.fileConnectionInfo?.sasUrl, storageFile.fileType);
      if (url) {
        const file = new FileDocument(
          this.getFileName(withInvestisseur),
          storageFile.createdDate,
          GedDocumentType.Der,
          url,
          this.derDocumentDescription,
          undefined,
          withInvestisseur ? this.investisseurService.coupleInvestisseurIds() : undefined
        );
        return { fileDocument: file };
      }
    }
    return null;
  }
  public async loadForceUnsignedDocument(): Promise<DerResponse | null> {
    const derData = await this.queryService.handleMutationErrorsAsync(
      this.forceDERUpdateGQL.mutate({
        variables: {
          investisseurId: this.investisseurService.investisseurSig()?.id,
          consultantCode: this.investisseurService.consultantCode(),
        },
      })
    );
    if (derData?.data?.forceDERUpdate) {
      const storageFile = derData.data.forceDERUpdate?.storageFiles?.[0];
      const fieldsToComplete = derData.data.forceDERUpdate?.fieldsToComplete;
      if (!storageFile) return { fieldsToComplete: fieldsToComplete ?? [] };
      const url = await this.fetchAndCacheDocument(storageFile.fileConnectionInfo?.sasUrl, storageFile.fileType);
      if (url) {
        const file = new FileDocument(
          this.getFileName(),
          storageFile.createdDate,
          GedDocumentType.Der,
          url,
          this.derDocumentDescription,
          undefined,
          this.investisseurService.coupleInvestisseurIds()
        );
        return { fileDocument: file };
      }
    }
    return null;
  }

  public async generateForOperation(investisseurId: number) {
    const result = await this.queryService.handleMutationErrorsAsync(
      this.forceDERUpdateGQL.mutate({
        fetchPolicy: 'network-only',
        variables: {
          investisseurId,
          consultantCode: this.investisseurService.consultantCode(),
        },
      })
    );
    const document = result.data?.forceDERUpdate?.storageFiles?.[0];
    if (!document?.fileConnectionInfo?.sasUrl) {
      throw new Error('Unable to generate DER document for investor.');
    }
    return document;
  }

  public getFakeDocument(): FileDocument {
    const document = new FileDocument(this.getFileName(), new Date(), GedDocumentType.Der, undefined, this.derDocumentDescription);
    document.isFakeFile = true;
    return document;
  }
  private async fetchAndCacheDocument(url?: string, fileType?: string): Promise<string | undefined> {
    if (url) {
      const res = await this.documentService.fetchAndCacheBlobDocument(url, fileType || 'application/pdf');
      if (res) {
        return res;
      }
    }
    return;
  }
  private getFileName(withInvestisseur: boolean = true): string {
    const investisseur = this.investisseurService.investisseurSig() as Investisseur | null;
    if (!withInvestisseur || !investisseur) {
      return `DER-${this.datePipe.transform(new Date(), 'dd_MM_yyyy')}.pdf`;
    }
    let filename = `DER-${investisseur.personnePhysique[0].nom.trim()}_${investisseur.personnePhysique[0].prenom.trim()}`;
    const conjoint = this.investisseurService.investisseurConjointSig() as Investisseur | null;
    if (conjoint) {
      filename += `&${conjoint.personnePhysique[0].nom.trim()}_${conjoint.personnePhysique[0].prenom.trim()}`;
    }
    return `${filename}-${this.datePipe.transform(new Date(), 'dd_MM_yyyy')}.pdf`.replace(' ', '');
  }
}
